import { Component, OnInit, Input } from '@angular/core';
import { DataService } from 'src/app/data.service';

declare var jsPDF: any;

@Component({
  selector: 'app-student-transcript',
  templateUrl: './student-transcript.component.html',
  styleUrls: ['./student-transcript.component.scss']
})
export class StudentTranscriptComponent implements OnInit {
  @Input() studentId: number;

  loading: boolean = false;
  transcriptData: any = null;

  constructor(private dataService: DataService) { }

  ngOnInit(): void {
    if (this.studentId) {
      this.loadTranscript();
    }
  }

  loadTranscript() {
    this.loading = true;
    this.dataService.getStudentTranscript(this.studentId).subscribe(
      (response: any) => {
        console.log('Transcript response:', response);
        if (response.status === 0) {
          this.transcriptData = response;
        } else {
          console.error('API returned error status:', response);
        }
        this.loading = false;
      },
      (error) => {
        console.error('Error loading transcript:', error);
        this.loading = false;
      }
    );
  }

  saveExamDetails(subject: any) {
    const payload = {
      score_id: subject.score_id,
      student_id: this.transcriptData.student.student_id, // Use ID from response if available, else fallback
      subject_id: subject.id,
      marks_awarded: subject.marks_awarded,
      passing_minimum: subject.passing_minimum,
      maximum_marks: subject.maximum_marks,
      month_year_passing: subject.month_year_passing
    };

    // We must ensure we have the internal student ID (primary key), not just the display "student_id" string.
    // The response `student` object usually has both? 
    // Checking PHP: 'student' => ['name' => ..., 'student_id' => ...] -> This is the display ID.
    // We need the internal PK. Let's pass it from the input since we have @Input() studentId.
    payload.student_id = this.studentId;

    this.dataService.saveTranscriptExam(payload).subscribe(
      (response: any) => {
        if (response.status === 0 && response.score_id) {
          subject.score_id = response.score_id; // Update ID for future edits
          console.log('Exam details saved');
        } else {
          console.error('Failed to save exam details:', response);
        }
      },
      (error) => {
        console.error('Error saving exam details:', error);
      }
    );
  }

  printTranscript() {
    const doc = new jsPDF();
    
    // Title
    doc.setFontSize(16);
    doc.text('TRANSCRIPT', 105, 20, { align: 'center' });
    
    // Student Information
    doc.setFontSize(12);
    doc.text('Name of the Candidate: ' + (this.transcriptData.student?.name || 'N/A'), 20, 35);
    doc.text('Class commencement Date: ' + (this.transcriptData.batch?.start_year || 'N/A'), 20, 45);
    
    // Academic Details Section
    doc.setFontSize(14);
    doc.text('ACADEMIC DETAILS', 105, 60, { align: 'center' });
    
    let startY = 70;
    
    if (this.transcriptData.academic_details && this.transcriptData.academic_details.length > 0) {
      this.transcriptData.academic_details.forEach((year: any) => {
        // Year header
        doc.setFontSize(11);
        doc.setFontStyle('bold');
        doc.text(year.semester_name, 20, startY);
        startY += 5;
        
        // Academic table
        if (year.subjects && year.subjects.length > 0) {
          const academicRows = year.subjects.map((subject: any, index: number) => [
            (index + 1).toString(),
            subject.name,
            (subject.theory_prescribed || 0).toString(),
            (subject.theory_attended || 0).toString(),
            (subject.practical_prescribed || 0).toString(),
            (subject.practical_attended || 0).toString()
          ]);
          
          doc.autoTable({
            startY: startY,
            head: [[
              { content: 'Sl. No', rowSpan: 2 },
              { content: 'Subjects', rowSpan: 2 },
              { content: 'Theory Hours', colSpan: 2 },
              { content: 'Practical Hours', colSpan: 2 }
            ], [
              'Prescribed', 'Attended', 'Prescribed', 'Attended'
            ]],
            body: academicRows,
            theme: 'grid',
            styles: { fontSize: 9 },
            headStyles: { fillColor: [200, 200, 200], textColor: [0, 0, 0] }
          });
          
          startY = doc.lastAutoTable.finalY + 10;
        }
      });
    }
    
    // Exam Details Section
    doc.setFontSize(14);
    doc.text('EXAM DETAILS', 105, startY, { align: 'center' });
    startY += 10;
    
    if (this.transcriptData.academic_details && this.transcriptData.academic_details.length > 0) {
      this.transcriptData.academic_details.forEach((year: any) => {
        // Year header
        doc.setFontSize(11);
        doc.setFontStyle('bold');
        doc.text(year.semester_name, 20, startY);
        startY += 5;
        
        // Exam table
        if (year.subjects && year.subjects.length > 0) {
          const examRows = year.subjects.map((subject: any, index: number) => [
            (index + 1).toString(),
            subject.name,
            subject.marks_awarded || '-',
            subject.passing_minimum || '-',
            subject.maximum_marks || '-',
            subject.month_year_passing || '-'
          ]);
          
          doc.autoTable({
            startY: startY,
            head: [['Sl No', 'Subject', 'Marks Awarded', 'Passing Minimum', 'Maximum Marks', 'Month and Year of Passing']],
            body: examRows,
            theme: 'grid',
            styles: { fontSize: 9 },
            headStyles: { fillColor: [200, 200, 200], textColor: [0, 0, 0] }
          });
          
          startY = doc.lastAutoTable.finalY + 10;
        }
      });
    }
    
    // Save the PDF
    const fileName = 'Transcript_' + (this.transcriptData.student?.name || 'Student').replace(/\s+/g, '_') + '.pdf';
    doc.save(fileName);
  }

}
